/**
 * 사진 촬영/갤러리 선택 공통 로직 hook
 *
 * 카메라 촬영과 갤러리 선택에 필요한 refs, 핸들러, hidden input 렌더링 props를 제공.
 * PhotoCaptureSlot, EmptyState 등 UI가 다른 컴포넌트에서 공통 사용.
 */

import { useRef, useCallback } from "react";

interface UseFileCaptureParams {
  /** 파일 선택 콜백 (카메라 촬영 또는 갤러리 단일 선택) */
  onFileSelect: (file: File) => void;
  /** 네이티브 인앱 카메라 열기 콜백 (네이티브 환경에서만 전달) */
  onCameraOpen?: () => void;
  /** 갤러리에서 다중 파일 선택 시 콜백 (galleryMultiple=true일 때 사용) */
  onMultiFileSelect?: (files: File[]) => void;
  /** 갤러리 input에서 다중 선택 허용 여부 (기본: false) */
  galleryMultiple?: boolean;
}

export function useFileCapture({
  onFileSelect,
  onCameraOpen,
  onMultiFileSelect,
  galleryMultiple = false,
}: UseFileCaptureParams) {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // 카메라 촬영 클릭: 네이티브면 InAppCamera, 웹이면 file input fallback
  const handleCameraClick = useCallback(() => {
    if (onCameraOpen) {
      onCameraOpen();
    } else {
      cameraInputRef.current?.click();
    }
  }, [onCameraOpen]);

  // 갤러리 선택 클릭
  const handleGalleryClick = useCallback(() => {
    galleryInputRef.current?.click();
  }, []);

  // 파일 선택 핸들러 (카메라/갤러리 공용)
  // 갤러리 다중 선택 시 onMultiFileSelect 호출, 그 외 단일 onFileSelect 호출
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) {
        e.target.value = "";
        return;
      }

      // 갤러리 input에서 다중 선택된 경우
      if (galleryMultiple && onMultiFileSelect && files.length > 1) {
        onMultiFileSelect(Array.from(files));
      } else {
        // 단일 파일 선택 (카메라 또는 갤러리 1장)
        const file = files[0];
        if (file) {
          onFileSelect(file);
        }
      }

      // input 값 초기화 (같은 파일 재선택 가능)
      e.target.value = "";
    },
    [onFileSelect, onMultiFileSelect, galleryMultiple]
  );

  return {
    cameraInputRef,
    galleryInputRef,
    handleCameraClick,
    handleGalleryClick,
    handleFileChange,
    /** HiddenFileInputs에 전달할 galleryMultiple 값 */
    galleryMultiple,
  };
}
